import produce from 'immer';

import {
  getLayoutInputNameForStepDataKind,
  LayoutInputDataName,
} from 'analysis-tool/data-view/RawDataViewRegistry';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import type {PipelineEdgeUpsertById} from 'pipelines/network/PipelineDAO';
import type {PipelineEdge, PipelineStep, PipelineViewGraphQL} from 'pipelines/PipelineTypes';
import {getOutputDataForView, resolveLineageStep, resolveOutputStepData} from 'pipelines/PipelineViewUtils';
import {getStepDataKindForRawData} from 'pipelines/RawStepDataKindRegistry';
import {getInputStepDataRawDatas} from 'pipelines/StepDataUtils';

import {
  AnalysisDataLayout,
  AnalysisDataView,
  AnalysisGridBlock,
  AnalysisPlaceholderBlock,
  LayoutStep,
} from './analysis-adapter/AnalysisAdapterTypes';
import {LayoutPath} from './DataLayoutTypes';
import {setAtLayoutPath} from './utils/LayoutPathUtils';

export type InputDataName = LayoutInputDataName;

export function getInputDataNameForView(
  view: PipelineViewGraphQL,
  steps: ReadonlyArray<PipelineStep>
): InputDataName {
  // The AnalysisViewSelector remaps CHART_VIEW output references from 'Output chart' to
  // 'Output dataset' (via mapChartViewsToDataFrameViews). Detect this case by checking the
  // source step type so chart views still route to 'Input charts'.
  const lastNode = view.lineage[view.lineage.length - 1];
  if (lastNode != null && resolveLineageStep(steps, lastNode).stepType === PipelineStepType.CHART_VIEW) {
    return 'Input charts';
  }
  const outputData = getOutputDataForView(view, steps);
  if (outputData == null) {
    return 'Input datasets';
  }
  // Resolve the kind with the non-asserting registry lookup so an unregistered
  // (e.g. newly-added) raw data kind degrades to the default rather than
  // throwing. The LAYOUT step only has dataset/chart/file slots, so kinds
  // without a slot also fall back to 'Input datasets'.
  const kind = getStepDataKindForRawData(outputData);
  return (kind != null ? getLayoutInputNameForStepDataKind(kind) : undefined) ?? 'Input datasets';
}

export function getNextInputDataIndex(layoutStep: LayoutStep, inputDataName: InputDataName): number {
  const rawDatasLength = getInputStepDataRawDatas(layoutStep, inputDataName).length;
  // Also check existing edge indices in case they are non-contiguous (e.g. after a view removal).
  // Use whichever is higher to avoid collisions.
  const existingEdgeIndices = (layoutStep.incomingEdges ?? [])
    .filter((edge) => edge.toStepData.name === inputDataName)
    .map((edge) => edge.toStepDataIndex)
    .filter((index): index is number => index != null);
  const maxEdgeIndex = existingEdgeIndices.length > 0 ? Math.max(...existingEdgeIndices) + 1 : 0;
  return Math.max(rawDatasLength, maxEdgeIndex);
}

export function convertEdgeToUpsert(edge: PipelineEdge): PipelineEdgeUpsertById {
  return {
    fromStepId: edge.fromStep.id,
    fromStepDataName: edge.fromStepData.name,
    fromStepDataIndex: edge.fromStepDataIndex,
    toStepDataName: edge.toStepData.name,
    toStepDataIndex: edge.toStepDataIndex,
    condition: edge.condition,
  };
}

export function buildAddViewEdge(
  view: PipelineViewGraphQL,
  steps: ReadonlyArray<PipelineStep>,
  inputDataName: InputDataName,
  inputDataIndex: number
): PipelineEdgeUpsertById | null {
  const lastNode = view.lineage[view.lineage.length - 1];
  if (lastNode?.stepOutputReference == null) {
    return null;
  }

  const resolvedOutputStepData = resolveOutputStepData(steps, {
    step: lastNode.step,
    stepOutputReference: lastNode.stepOutputReference,
  });
  let fromStepDataName = resolvedOutputStepData.name;
  let fromStepDataIndex = lastNode.stepOutputReference.rawDataIndex;

  // The AnalysisViewSelector remaps CHART_VIEW output references from 'Output chart' to
  // 'Output dataset' (via mapChartViewsToDataFrameViews). When routing to 'Input charts',
  // redirect the edge to use 'Output chart' to ensure correct data kind matching.
  if (inputDataName === 'Input charts' && fromStepDataName !== 'Output chart') {
    const resolvedStep = resolveLineageStep(steps, lastNode);
    const chartOutput = resolvedStep.outputStepDatas.find((d) => d.name === 'Output chart');
    if (chartOutput != null) {
      fromStepDataName = 'Output chart';
      fromStepDataIndex = 0;
    }
  }

  return {
    fromStepId: lastNode.step.id,
    fromStepDataName,
    fromStepDataIndex,
    toStepDataName: inputDataName,
    toStepDataIndex: inputDataIndex,
  };
}

/**
 * Ensure all edges for the same `toStepDataName` have concrete numeric indices.
 * The backend rejects a mix of null and non-null `toStepDataIndex` values for the
 * same step data name (see `_validate_and_sort_edge_upserts` in pipeline_step_handler.py).
 * Layout edges should always have concrete indices, but this normalizes defensively
 * in case any existing edges were stored with null indices.
 */
export function normalizeEdgeIndices(edges: Array<PipelineEdgeUpsertById>): Array<PipelineEdgeUpsertById> {
  const grouped = new Map<string, Array<PipelineEdgeUpsertById>>();
  for (const edge of edges) {
    if (!grouped.has(edge.toStepDataName)) {
      grouped.set(edge.toStepDataName, []);
    }
    grouped.get(edge.toStepDataName)!.push(edge);
  }

  return [...grouped.values()].flatMap((group) => {
    if (group.every((e) => e.toStepDataIndex != null)) {
      return group;
    }
    // Preserve existing non-null indices; only assign indices for null edges after the current max.
    const concreteIndices = group.filter((e) => e.toStepDataIndex != null).map((e) => e.toStepDataIndex!);
    let nextIndex = concreteIndices.length > 0 ? Math.max(...concreteIndices) + 1 : 0;
    return group.map((edge) =>
      edge.toStepDataIndex != null ? edge : {...edge, toStepDataIndex: nextIndex++}
    );
  });
}

/**
 * Collect all inputReferences from DATA blocks in the given sections.
 * Works on backend config sections where DATA blocks have `inputReference`.
 */
export function collectInputReferences(
  sections: AnalysisDataLayout['sections']
): Array<AnalysisDataView['inputReference']> {
  const refs: Array<AnalysisDataView['inputReference']> = [];

  function walk(blocks: ReadonlyArray<AnalysisGridBlock>): void {
    for (const block of blocks) {
      if (block.type === 'DATA') {
        refs.push({
          inputDataName: block.inputReference.inputDataName,
          inputDataIndex: block.inputReference.inputDataIndex,
        });
      } else if (block.type === 'NESTED_GRID') {
        for (const nestedRow of block.rows) {
          walk(nestedRow);
        }
      }
    }
  }

  for (const section of sections) {
    const rows = section.type === 'GRID' ? section.rows : section.tabs.flatMap((tab) => tab.rows);
    rows.forEach((row) => {
      walk(row);
    });
  }
  return refs;
}

/**
 * Filter a layout step's incoming edges to only those still referenced by DATA blocks in the config.
 */
export function filterEdgesToRemainingRefs(
  incomingEdges: Array<PipelineEdge>,
  sections: AnalysisDataLayout['sections']
): Array<PipelineEdgeUpsertById> {
  const remainingRefs = collectInputReferences(sections);
  return incomingEdges
    .map((edge) => convertEdgeToUpsert(edge))
    .filter((edge) =>
      remainingRefs.some(
        (ref) => ref.inputDataName === edge.toStepDataName && ref.inputDataIndex === edge.toStepDataIndex
      )
    );
}

export function replaceBlockInConfig<T extends {sections: AnalysisDataLayout['sections']}>(
  config: T,
  layoutPath: LayoutPath,
  newBlock: Pick<AnalysisDataView, 'type' | 'inputReference'> | Pick<AnalysisPlaceholderBlock, 'type'>
): T {
  return produce(config, (draft) => {
    setAtLayoutPath(draft, layoutPath, newBlock);
  });
}
